import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import classnames from 'classnames/bind';
import { Filter } from 'lucide-react';

import { AdminSingleSectionData, AdminSingleSectionMeta, AdminSingleSectionDataVariant } from '@INTERFACES/admin-api/sections';
import useDocumentPermissions from '@HOOKS/useDocumentPermissions';
import useNewVariantModal from '@HOOKS/store/modals/useNewVariantModal';

import VariantFilters from '@PAGES/admin/edit-section/components/VariantFilters';
import { VariantFiltersData as VariantFiltersType, FilterOption } from '@PAGES/admin/edit-section/components/VariantFilters/VariantFilters.component';
import VariantListCard from '@PAGES/admin/edit-section/components/VariantListCard';
import AddButton from '@COMPONENTS/COMMON/buttons/AddButton';

import styles from './VariantListSidebar.module.scss';

const cx: CX = classnames.bind(styles);

interface Props {
    sectionData: AdminSingleSectionData;
    sectionMeta: AdminSingleSectionMeta;
    selectedVariantId: number | null;
    onVariantSelect: (variantId: number) => void;
    variantDefaults: { [key: number]: boolean };
}

function VariantListSidebar(props: Props) {
    const {
        sectionData,
        sectionMeta,
        selectedVariantId,
        onVariantSelect,
        variantDefaults,
    } = props;
    const navigate = useNavigate();
    const { hash } = useLocation();
    
    const { canEditDocument } = useDocumentPermissions({
        documentOwnerId: sectionData.document.owner,
        permissions: sectionMeta.permissions,
    });

    const { actions: newVariantModalActions } = useNewVariantModal();

    const [filters, setFilters] = useState<VariantFiltersType>({
        brand: null,
        region: null,
        area: null,
        country: null,
        propertyType: null,
    });

    const [isFiltersExpanded, setIsFiltersExpanded] = useState<boolean>(false);

    // Sort variants: default first, then by name/id
    const sortedVariants = useMemo(() => sectionData.variants.slice().sort((a, b) => {
        const isADefault = variantDefaults[a.id] || a.isDefault;
        const isBDefault = variantDefaults[b.id] || b.isDefault;

        if (isADefault && !isBDefault) return -1;
        if (!isADefault && isBDefault) return 1;

        // Then sort by name or ID
        const aName = a.name || `Variant ${a.id}`;
        const bName = b.name || `Variant ${b.id}`;
        return aName.localeCompare(bName);
    }), [sectionData.variants, variantDefaults]);

    // Filter variants based on selected filters
    const filteredVariants = useMemo(() => {
        if (!filters.brand && !filters.region && !filters.area && !filters.country && !filters.propertyType) {
            return sortedVariants;
        }

        return sortedVariants.filter((variant) => {
            // Default variant matches all filters
            if (variantDefaults[variant.id] || variant.isDefault) {
                return true;
            }

            // Check each filter
            if (filters.brand && !variant.brands.some((b) => b.id === filters.brand!.value)) {
                return false;
            }

            if (filters.region && !variant.regions.some((r) => r.id === filters.region!.value)) {
                return false;
            }

            if (filters.area && !variant.areas.some((a) => a.id === filters.area!.value)) {
                return false;
            }

            if (filters.country && !variant.countries.some((c) => c.id === filters.country!.value)) {
                return false;
            }

            if (filters.propertyType && !variant.propertyTypes.some((pt) => pt.id === filters.propertyType!.value)) {
                return false;
            }

            return true;
        });
    }, [sortedVariants, filters, variantDefaults]);

    const handleClearFilters = () => {
        setFilters({
            brand: null,
            region: null,
            area: null,
            country: null,
            propertyType: null,
        });
    };

    const handleVariantClick = (variantId: number) => {
        onVariantSelect(variantId);
        
        // Update URL hash for navigation
        const newHash = `#variant-${variantId}`;
        if (hash !== newHash) {
            navigate(`${window.location.pathname}${window.location.search}${newHash}`, { replace: true });
        }
    };

    const handleAddVariant = () => {
        newVariantModalActions.open({
            documentId: sectionData.document.id,
            chapterId: sectionData.chapter.id,
            sectionId: sectionData.id,
            canEditDocument,
        });
    };

    const hasActiveFilters = useMemo(
        () => Object.values(filters).some((filter) => filter !== null), 
        [filters],
    );

    const toggleFilters = () => {
        setIsFiltersExpanded(!isFiltersExpanded);
    };

    return (
        <div className={cx('variant-list-sidebar')}>
            <div className={cx('sidebar-header')}>
                <h2 className={cx('sidebar-title')}>
                    Section Variants (
                    {sectionData.variants.length}
                    )
                </h2>
            </div>

            <div className={cx('filters-section')}>
                <div className={cx('filters-toggle-wrapper')}>
                    <button
                        type="button"
                        className={cx('filter-toggle-btn', { 
                            'has-filters': hasActiveFilters,
                            expanded: isFiltersExpanded, 
                        })}
                        onClick={toggleFilters}
                    >
                        <Filter size={16} className={cx('filter-icon')} />
                        <span className={cx('filter-text')}>
                            Filter Variants
                            {hasActiveFilters && (
                                <span className={cx('filter-count')}>
                                    (
                                    {Object.values(filters).filter((f) => f !== null).length}
                                    )
                                </span>
                            )}
                        </span>
                    </button>
                </div>
                
                {isFiltersExpanded && (
                    <div className={cx('filters-content')}>
                        <VariantFilters
                            sectionMeta={sectionMeta}
                            filters={filters}
                            onFiltersChange={setFilters}
                            onClearFilters={handleClearFilters}
                        />
                    </div>
                )}
            </div>

            <div className={cx('variants-section')}>
                <div className={cx('variants-header')}>
                    <div className={cx('variants-header-content')}>
                        <span className={cx('variants-count')}>
                            {filteredVariants.length}
                            {' '}
                            of
                            {' '}
                            {sectionData.variants.length}
                            {' '}
                            variants
                        </span>
                        
                        <div className={cx('add-variant-btn')}>
                            <AddButton
                                locked={!canEditDocument}
                                title="New variant"
                                fontSize={12}
                                size="small"
                                onClick={handleAddVariant}
                            />
                        </div>
                    </div>
                </div>

                <div className={cx('variants-list')}>
                    {filteredVariants.length > 0 ? (
                        filteredVariants.map((variant) => (
                            <VariantListCard
                                key={variant.id}
                                variant={{
                                    ...variant,
                                    isDefault: variantDefaults[variant.id] || variant.isDefault,
                                }}
                                isSelected={selectedVariantId === variant.id}
                                onClick={() => handleVariantClick(variant.id)}
                            />
                        ))
                    ) : (
                        <div className={cx('empty-state')}>
                            <div className={cx('empty-icon')}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <circle cx="11" cy="11" r="8" />
                                    <path d="m21 21-4.35-4.35" />
                                </svg>
                            </div>
                            <div className={cx('empty-title')}>No Variants Found</div>
                            <div className={cx('empty-description')}>
                                Try adjusting your filters or add a new variant to get started.
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default VariantListSidebar; 